import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '@/storybook/components/Badge/Badge';
import TruncatedText from '@/storybook/components/TruncatedText/TruncatedText';
import { CustomColumnDef } from '@/types/table';
import { UserFilterListItem } from '@/models/user/userFilter';
import { toFormattedDateOrNull, DateFormat } from '@/utils/date/format';
import FilterActionsMenu from './FilterActionsMenu';

import styles from './ManageSavedFiltersDrawer.module.css';

interface UseManageFiltersColumnsParams {
  selectedFilterId: string | null;
  onSelect: (filterId: string) => void;
  onSeeDetails: (filter: UserFilterListItem) => void;
  onRename: (filter: UserFilterListItem) => void;
  onDelete: (filter: UserFilterListItem) => void;
  onSetAsDefault: (filter: UserFilterListItem) => void;
  onDeselectDefault: (filter: UserFilterListItem) => void;
  onShareFilter: (filter: UserFilterListItem) => void;
}

const useManageFiltersColumns = ({
  selectedFilterId,
  onSelect,
  onSeeDetails,
  onRename,
  onDelete,
  onSetAsDefault,
  onDeselectDefault,
  onShareFilter,
}: UseManageFiltersColumnsParams): CustomColumnDef<UserFilterListItem>[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        header: t('global.filters.manageSavedFilters.columnSelectedFilter'),
        id: 'selection',
        enableSorting: false,
        size: 130,
        cell: ({ row }) => {
          const savedFilter = row.original;
          return (
            <input
              type="radio"
              name="saved-filter-selection"
              className={styles.radioButton}
              checked={selectedFilterId === savedFilter.userFilterId}
              onChange={() => onSelect(savedFilter.userFilterId)}
              aria-label={savedFilter.filterName ?? savedFilter.userFilterId}
            />
          );
        },
      },
      {
        header: t('global.filters.manageSavedFilters.columnName'),
        id: 'filterName',
        accessorFn: (row: UserFilterListItem) => row.filterName ?? row.userFilterId,
        cell: ({ row }) => {
          const savedFilter = row.original;
          const name = savedFilter.filterName ?? savedFilter.userFilterId;
          return (
            <div className={styles.nameCell}>
              <TruncatedText value={name} />
              {savedFilter.isDefault && (
                <Badge variant="filled" color="blue" size="S">
                  {t('global.filters.manageSavedFilters.defaultBadge')}
                </Badge>
              )}
            </div>
          );
        },
        sortDescFirst: false,
      },
      {
        header: t('global.filters.manageSavedFilters.columnLastModified'),
        id: 'lastModified',
        accessorFn: (row: UserFilterListItem) => row.filterDate,
        cell: ({ row }) => toFormattedDateOrNull(row.original.filterDate, DateFormat.dateFormat, '-'),
        sortDescFirst: true,
      },
      {
        header: t('global.filters.manageSavedFilters.columnDateCreated'),
        id: 'dateCreated',
        accessorFn: (row: UserFilterListItem) => row.filterDate,
        cell: ({ row }) => toFormattedDateOrNull(row.original.filterDate, DateFormat.dateFormat, '-'),
        sortDescFirst: true,
      },
      {
        header: t('global.filters.manageSavedFilters.columnActions'),
        id: 'actions',
        enableSorting: false,
        size: 280,
        cell: ({ row }) => {
          const savedFilter = row.original;
          const isDefault = savedFilter.isDefault === true;
          return (
            <div className={styles.actionsCell}>
              <button
                type="button"
                className={styles.actionLink}
                onClick={() => (isDefault ? onDeselectDefault(savedFilter) : onSetAsDefault(savedFilter))}
              >
                {isDefault
                  ? t('global.filters.manageSavedFilters.deselectDefault')
                  : t('global.filters.manageSavedFilters.setAsDefault')}
              </button>
              <button type="button" className={styles.actionLink} onClick={() => onShareFilter(savedFilter)}>
                {t('global.filters.manageSavedFilters.shareFilters')}
              </button>
              <FilterActionsMenu
                onSeeDetails={() => onSeeDetails(savedFilter)}
                onRename={() => onRename(savedFilter)}
                onDelete={() => onDelete(savedFilter)}
              />
            </div>
          );
        },
      },
    ],
    [t, selectedFilterId, onSelect, onSeeDetails, onRename, onDelete, onSetAsDefault, onDeselectDefault, onShareFilter],
  );
};

export default useManageFiltersColumns;
